/* Public Rate Schedule view — token-protected, no auth required.
 * The recipient reads every clause, initials each section, signs both blocks,
 * and submits. The hosting franchise sees status updates on the Documents board. */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Eraser, Printer, Warning, PaperPlaneTilt, ChatCircleText, Buildings, DownloadSimple, CircleNotch } from "@phosphor-icons/react";
import { RoomScopeSummary } from "./calculator/RoomScopeSummary";
import { groupRoomsByFloor, DEMO_LEVEL_LABEL } from "./calculator/helpers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function ShareView() {
    const { token } = useParams();
    const [doc, setDoc] = useState(null);
    const [err, setErr] = useState("");
    const [printedName, setPrintedName] = useState("");
    const [initials, setInitials] = useState({});
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const clientCanvas = useRef(null);
    const providerCanvas = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${BACKEND_URL}/api/public/documents/${token}`);
                setDoc(data);
                if (data?.status === "signed") setDone(true);
            } catch (e) {
                setErr(e?.response?.data?.detail || "Document not found or expired");
            }
        })();
    }, [token]);

    async function submit() {
        const clientSig = canvasToData(clientCanvas);
        const providerSig = canvasToData(providerCanvas);
        if (!clientSig || !printedName.trim()) {
            setErr("Please sign and print your name before submitting.");
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${BACKEND_URL}/api/public/documents/${token}/sign`, {
                section_initials: initials,
                client_signature: { data_url: clientSig },
                provider_signature: providerSig ? { data_url: providerSig } : null,
                client_printed_name: printedName,
                signed_date: new Date().toISOString(),
            });
            setDone(true);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Failed to submit signature");
        }
        setBusy(false);
    }

    if (err && !doc) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6 bg-[var(--rom-bg)]">
                <div className="rom-card max-w-md">
                    <div className="rom-card-body text-center">
                        <Warning size={32} className="mx-auto text-[var(--rom-danger)] mb-3" />
                        <h2 className="font-display font-bold text-lg">Link unavailable</h2>
                        <p className="text-sm text-[var(--rom-text-2)] mt-2">{err}</p>
                    </div>
                </div>
            </div>
        );
    }
    if (!doc) return <div className="min-h-screen flex items-center justify-center text-[var(--rom-text-2)]">Loading…</div>;

    if (doc.kind === "rom_proposal") {
        return <ProposalShareView doc={doc} token={token} />;
    }
    if (doc.kind === "invoice") {
        return <InvoiceShareView doc={doc} token={token} />;
    }

    const sch = doc.payload?.schedule_snapshot || {};
    const text = sch.conditions_text || {};
    const sectionsForInitial = [
        { key: "labour", label: "Labour conditions" },
        { key: "consumables", label: "Consumables conditions" },
        { key: "equipment", label: "Equipment conditions" },
        { key: "general", label: "General terms" },
        { key: "prime_area", label: "Prime Area" },
    ];

    return (
        <div className="min-h-screen bg-[var(--rom-bg)] pb-12">
            <div className="bg-[var(--rom-navy)] text-white no-print">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
                    <img src="/servpro-logo.png" alt="SERVPRO" className="w-10 h-10 object-contain" />
                    <div className="flex-1">
                        <div className="font-display font-black text-base">SERVPRO Rate Schedule</div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">For signature · {doc.payload?.recipient_name || "client"}</div>
                    </div>
                    <button onClick={() => window.print()} className="btn btn-ghost text-white border-white/30 hover:bg-white/10" data-testid="share-print">
                        <Printer size={14} weight="bold" /> Print
                    </button>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-6 py-8 print-area">
                {done && (
                    <div className="rom-card mb-6 border-l-4 border-l-[var(--rom-green)]" data-testid="share-done">
                        <div className="rom-card-body flex items-center gap-3 text-[var(--rom-green)]">
                            <CheckCircle size={22} weight="fill" />
                            <div>
                                <div className="font-bold">Thanks — this rate schedule has been signed.</div>
                                <div className="text-xs text-[var(--rom-text-2)]">A countersigned copy is available on the SERVPRO Documents board.</div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="rom-card mb-6">
                    <div className="rom-card-body">
                        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
                            <div>
                                <h1 className="font-display font-black text-2xl">{sch.name}</h1>
                                <div className="text-sm text-[var(--rom-text-2)]">Effective {sch.effective_date} · v{sch.version}</div>
                            </div>
                            <span className={`chip ${done ? "chip-green" : "chip-orange"}`}>
                                {done ? "Signed" : "Awaiting signature"}
                            </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm border-t border-[var(--rom-border)] pt-4">
                            <Field k="Project address" v={doc.payload?.project_address} />
                            <Field k="Franchise" v={doc.payload?.franchise_legal_name} />
                            <Field k="City, State, Zip" v={doc.payload?.city_state_zip} />
                            <Field k="Provider rep" v={doc.payload?.provider_name} />
                        </div>
                    </div>
                </div>

                {/* Schedule A */}
                <DocSection title="Schedule A · Labour Rates">
                    <table className="rom-table">
                        <thead><tr><th>Role</th><th className="numeric">Standard $/hr</th><th className="numeric">Prime Area $/hr</th><th className="numeric">OT mult</th></tr></thead>
                        <tbody>{(sch.schedule_a || []).map((r, i) => (
                            <tr key={i}><td>{r.role}</td><td className="numeric">${Number(r.standard).toFixed(2)}</td><td className="numeric">${Number(r.prime).toFixed(2)}</td><td className="numeric">{r.ot_mult}×</td></tr>
                        ))}</tbody>
                    </table>
                </DocSection>

                {/* Schedule B */}
                <DocSection title="Schedule B · Consumables">
                    <table className="rom-table">
                        <thead><tr><th>Item</th><th>Unit</th></tr></thead>
                        <tbody>{(sch.schedule_b || []).map((r, i) => (
                            <tr key={i}><td>{r.item}</td><td>{r.unit}</td></tr>
                        ))}</tbody>
                    </table>
                </DocSection>

                {/* Schedule C */}
                <DocSection title="Schedule C · Equipment">
                    <table className="rom-table">
                        <thead><tr><th>Item</th><th className="numeric">$/day</th><th>Unit</th></tr></thead>
                        <tbody>{(sch.schedule_c || []).map((r, i) => (
                            <tr key={i}><td>{r.item}</td><td className="numeric">${Number(r.rate_per_day).toFixed(2)}</td><td>{r.unit}</td></tr>
                        ))}</tbody>
                    </table>
                </DocSection>

                {/* Conditions narrative sections — each has its own initial box */}
                <ClauseSection
                    title="Labour Conditions"
                    paragraphs={text.labour || []}
                    section="labour"
                    onInitial={(v) => setInitials((p) => ({ ...p, labour: v }))}
                    initialValue={initials.labour}
                    readonly={done}
                />
                <ClauseSection
                    title="Consumables Conditions"
                    paragraphs={text.consumables || []}
                    section="consumables"
                    onInitial={(v) => setInitials((p) => ({ ...p, consumables: v }))}
                    initialValue={initials.consumables}
                    readonly={done}
                />
                <ClauseSection
                    title="Equipment Conditions"
                    paragraphs={text.equipment || []}
                    section="equipment"
                    onInitial={(v) => setInitials((p) => ({ ...p, equipment: v }))}
                    initialValue={initials.equipment}
                    readonly={done}
                />
                <ClauseSection
                    title="General Terms"
                    paragraphs={text.general || []}
                    section="general"
                    onInitial={(v) => setInitials((p) => ({ ...p, general: v }))}
                    initialValue={initials.general}
                    readonly={done}
                />
                {text.prime_area && (
                    <ClauseSection
                        title="Prime Area"
                        paragraphs={[text.prime_area]}
                        section="prime_area"
                        onInitial={(v) => setInitials((p) => ({ ...p, prime_area: v }))}
                        initialValue={initials.prime_area}
                        readonly={done}
                    />
                )}

                {/* Signature block */}
                <DocSection title="Signature">
                    {!done ? (
                        <div className="rom-card-body space-y-5">
                            <div>
                                <label className="label">Client printed name</label>
                                <input className="input" value={printedName} onChange={(e) => setPrintedName(e.target.value)} placeholder="Full legal name" data-testid="share-printed-name" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <SignatureField label="Client signature" canvasRef={clientCanvas} testId="share-client-sig" />
                                <SignatureField label="Provider signature (optional)" canvasRef={providerCanvas} testId="share-provider-sig" />
                            </div>
                            <p className="text-[11px] text-[var(--rom-text-2)] leading-relaxed">
                                By signing above I acknowledge I have read each section, initialled where indicated, and agree to the rates and conditions of this Time & Materials Rate Schedule. The provider is an independently owned and operated SERVPRO franchise.
                            </p>
                            {err && <div className="text-sm text-[var(--rom-danger)]">{err}</div>}
                            <div className="flex justify-end">
                                <button className="btn btn-primary" onClick={submit} disabled={busy} data-testid="share-submit">
                                    <CheckCircle size={14} weight="bold" /> {busy ? "Submitting…" : "Submit signed rate schedule"}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="rom-card-body space-y-3 text-sm">
                            <div>Signed by <b>{doc.payload?.client_printed_name}</b> on {fmt(doc.payload?.signed_date)}.</div>
                            {doc.payload?.client_signature?.data_url && (
                                <img src={doc.payload.client_signature.data_url} alt="Client signature" className="border border-[var(--rom-border)] max-h-[120px] bg-white p-2 rounded" />
                            )}
                        </div>
                    )}
                </DocSection>

                <p className="text-[11px] text-center text-[var(--rom-text-2)] mt-8 italic">
                    {text.footer_note || "Provider is an independently owned and operated SERVPRO franchise."}
                </p>
            </div>
        </div>
    );
}

function DocSection({ title, children }) {
    return (
        <div className="rom-card mb-6" data-testid={`doc-section-${title}`}>
            <div className="rom-card-head"><h3>{title}</h3></div>
            <div>{children}</div>
        </div>
    );
}

function ClauseSection({ title, paragraphs, section, onInitial, initialValue, readonly }) {
    return (
        <div className="rom-card mb-6" data-testid={`clause-${section}`}>
            <div className="rom-card-head"><h3>{title}</h3>
                <div className="text-[11px] text-[var(--rom-text-2)] uppercase tracking-wider">
                    {readonly
                        ? <span>Initialled · <b className="font-mono text-[var(--rom-text)]">{initialValue}</b></span>
                        : "Read & initial below"}
                </div>
            </div>
            <div className="rom-card-body space-y-3">
                {paragraphs.map((p, i) => (
                    <p key={i} className="text-sm leading-relaxed">{p}</p>
                ))}
                {!readonly && (
                    <div className="flex items-center gap-3 pt-3 border-t border-dashed border-[var(--rom-border)]">
                        <span className="text-[11px] uppercase tracking-wider text-[var(--rom-text-2)] font-semibold">Initial</span>
                        <input
                            maxLength={4}
                            placeholder="e.g. AB"
                            value={initialValue || ""}
                            onChange={(e) => onInitial(e.target.value.toUpperCase())}
                            className="input font-mono uppercase max-w-[100px] text-center font-bold tracking-widest"
                            data-testid={`initial-${section}`}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

function SignatureField({ label, canvasRef, testId }) {
    const [hasInk, setHasInk] = useState(false);
    const [drawing, setDrawing] = useState(false);
    useEffect(() => {
        if (!canvasRef.current) return;
        const ctx = canvasRef.current.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctx.strokeStyle = "#0E1A24";
        ctx.lineWidth = 2.2;
        ctx.lineCap = "round";
    }, [canvasRef]);

    const point = (e) => {
        const r = canvasRef.current.getBoundingClientRect();
        const t = e.touches ? e.touches[0] : e;
        return { x: t.clientX - r.left, y: t.clientY - r.top };
    };
    const start = (e) => { e.preventDefault(); const c = canvasRef.current.getContext("2d"); const p = point(e); c.beginPath(); c.moveTo(p.x, p.y); setDrawing(true); };
    const move = (e) => { if (!drawing) return; e.preventDefault(); const c = canvasRef.current.getContext("2d"); const p = point(e); c.lineTo(p.x, p.y); c.stroke(); setHasInk(true); };
    const end = () => setDrawing(false);
    const clear = () => { const c = canvasRef.current.getContext("2d"); c.fillStyle = "#ffffff"; c.fillRect(0,0, canvasRef.current.width, canvasRef.current.height); setHasInk(false); };

    return (
        <div>
            <label className="label">{label}</label>
            <div className="border-2 border-dashed border-[var(--rom-border-strong)] rounded-[6px] bg-white">
                <canvas
                    ref={canvasRef}
                    width={600}
                    height={160}
                    style={{ height: 160 }}
                    className="w-full block touch-none cursor-crosshair"
                    onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                    onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                    data-testid={testId}
                />
            </div>
            <button onClick={clear} className="btn btn-ghost btn-sm mt-2" type="button">
                <Eraser size={12} weight="bold" /> Clear
            </button>
            {!hasInk && <span className="text-[11px] text-[var(--rom-text-3)] ml-2">Empty</span>}
        </div>
    );
}

function Field({ k, v }) {
    return <div><span className="text-[var(--rom-text-2)]">{k}:</span> <span className="font-medium ml-1">{v || "—"}</span></div>;
}

const PROP_LOSS_LABEL = { water: "Water Damage", fire: "Fire & Smoke", mould: "Mould Remediation", storm: "Storm Damage" };
function money(n) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Number(n) || 0);
}
const RECIP_LABEL = { adjuster: "Adjuster", insurer: "Insurance", client: "Client" };

const INVOICE_STATUS_META = {
    draft: { cls: "chip-gray", label: "Draft" },
    sent: { cls: "chip-orange", label: "Awaiting payment" },
    partially_paid: { cls: "chip-navy", label: "Partially paid" },
    paid: { cls: "chip-green", label: "Paid in full" },
    overdue: { cls: "chip-orange", label: "Overdue" },
};

function InvoiceShareView({ doc, token }) {
    const [payMsg, setPayMsg] = useState("");
    const [paying, setPaying] = useState(false);
    const p = doc.payload || {};
    const meta = INVOICE_STATUS_META[p.display_status] || INVOICE_STATUS_META.sent;
    const balance = p.balance ?? p.total;

    async function payNow() {
        setPaying(true);
        try {
            const { data } = await axios.post(`${BACKEND_URL}/api/public/documents/${token}/pay-mock`);
            setPayMsg(data.message);
        } catch {
            setPayMsg("Something went wrong — please try again shortly.");
        } finally { setPaying(false); }
    }

    return (
        <div className="min-h-screen bg-[var(--rom-bg)] pb-12">
            <div className="bg-[var(--rom-navy)] text-white no-print">
                <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-4">
                    <img src="/servpro-logo.png" alt="SERVPRO" className="w-10 h-10 object-contain" />
                    <div className="flex-1">
                        <div className="font-display font-black text-base">Invoice {p.invoice_number}</div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">{p.project_name}{p.recipient_name ? ` · Attn: ${p.recipient_name}` : ""}</div>
                    </div>
                    <button onClick={() => window.print()} className="btn btn-ghost text-white border-white/30 hover:bg-white/10" data-testid="invoice-share-print">
                        <Printer size={14} weight="bold" /> Print
                    </button>
                </div>
            </div>

            <div className="max-w-2xl mx-auto px-6 py-8 print-area space-y-5">
                <div className="rom-card">
                    <div className="rom-card-body space-y-4">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--rom-text-2)] font-semibold">Amount due</div>
                                <div className="font-display font-black text-4xl tracking-tight mt-1" data-testid="invoice-share-total">{money(p.total)}</div>
                                <div className="text-xs text-[var(--rom-text-3)] mt-1">Due {p.due_date}{p.jurisdiction ? ` · ${p.jurisdiction}` : ""}</div>
                            </div>
                            <span className={`chip ${meta.cls}`} data-testid="invoice-share-status">{meta.label}</span>
                        </div>
                        <table className="rom-table">
                            <thead><tr><th>Description</th><th className="numeric">Amount</th></tr></thead>
                            <tbody>
                                {(p.line_items || []).map((li) => (
                                    <tr key={li.id}><td>{li.description}</td><td className="numeric font-mono">{money(li.amount)}</td></tr>
                                ))}
                                <tr><td className="text-[var(--rom-text-2)]">Subtotal</td><td className="numeric font-mono">{money(p.subtotal)}</td></tr>
                                {p.tax_amount > 0 && <tr><td className="text-[var(--rom-text-2)]">Tax ({p.tax_rate}%)</td><td className="numeric font-mono">{money(p.tax_amount)}</td></tr>}
                                <tr className="font-bold"><td>Total</td><td className="numeric font-mono">{money(p.total)}</td></tr>
                                {p.paid_amount > 0 && (
                                    <>
                                        <tr className="text-[var(--rom-green)]"><td>Paid</td><td className="numeric font-mono">−{money(p.paid_amount)}</td></tr>
                                        <tr className="font-bold"><td>Balance due</td><td className="numeric font-mono">{money(balance)}</td></tr>
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {balance > 0.01 && (
                    <div className="rom-card no-print">
                        <div className="rom-card-body text-center space-y-3">
                            {payMsg ? (
                                <div className="text-sm text-[var(--rom-navy)] font-medium" data-testid="invoice-pay-message">{payMsg}</div>
                            ) : (
                                <>
                                    <button onClick={payNow} disabled={paying} className="btn btn-primary" data-testid="invoice-pay-now-btn">
                                        {paying ? <CircleNotch size={15} className="animate-spin" /> : <PaperPlaneTilt size={15} weight="bold" />} {paying ? "Processing…" : `Pay Now — ${money(balance)}`}
                                    </button>
                                    <div className="text-[11px] text-[var(--rom-text-3)]">Prefer to pay another way? Contact your project manager to arrange a bank transfer or check.</div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ProposalShareView({ doc, token }) {
    const [status, setStatus] = useState(doc.status);
    const [mode, setMode] = useState(null); // null | "accept" | "changes"
    const [printedName, setPrintedName] = useState("");
    const [message, setMessage] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [signedName, setSignedName] = useState("");
    const [downloading, setDownloading] = useState(false);
    const [authorizedAmount, setAuthorizedAmount] = useState("");
    const sigCanvas = useRef(null);
    const docRef = useRef(null);

    const p = doc.payload || {};
    const r = p.result || {};
    const inputs = p.inputs || {};
    const recip = RECIP_LABEL[p.recipient_type] || "Client";
    const lossLabel = PROP_LOSS_LABEL[inputs.lossType] || "Restoration";
    const done = status === "accepted";
    const requested = status === "changes_requested";

    // Document track: client = price Quote (Track A); adjuster/insurer = ceiling
    // Not-To-Exceed authorization (Track B). Math is identical — only framing forks.
    const isCeiling = p.recipient_type === "adjuster" || p.recipient_type === "insurer";
    const scale = r.property_scale || inputs.propertyScale || "commercial";
    const isResidential = scale === "residential";
    const calibrated = r.band_source === "calibrated" || r.band_source === "dynamic";
    const docTitle = isCeiling ? `${lossLabel} — Work Authorization Request` : `${lossLabel} Restoration Quote`;
    const summaryTitle = isCeiling ? "Authorization Request Summary" : "Quote Summary";
    const ceiling = r.band_high ?? grand;

    const buckets = [
        ["Labour & Supervision", r.labour_total],
        ["Consumables & PPE", r.consumables_total],
        ["Equipment", r.equipment_total],
        ["Subcontractors", r.subcontractor_total ?? r.subs_total],
        ["Mobilisation & Incidentals", r.incidentals_total],
    ].filter(([, v]) => Number(v) > 0);
    const grand = r.grand_total ?? p.grand_total ?? 0;

    // Flaggable items for line-level "request changes" — built from what the
    // recipient actually sees on the document (cost sections + timeline phases,
    // or cleaning groups). Lets them pinpoint exactly what they're contesting.
    const flagItems = useMemo(() => {
        const items = [];
        if (r.mode === "cleaning") {
            Object.entries(r.groups || {}).forEach(([g, v]) => items.push({ key: `group:${g}`, label: g, amount: v, money: true }));
        } else {
            buckets.forEach(([label, val]) => items.push({ key: `bucket:${label}`, label, amount: val, money: true }));
            (r.timeline || []).forEach((t) => items.push({ key: `phase:${t.id}`, label: `Timeline · ${t.phase}`, amount: null, money: false }));
        }
        return items;
    }, [r, buckets]);
    const [flags, setFlags] = useState({});
    const setFlag = (key, patch) => setFlags((f) => ({ ...f, [key]: { ...(f[key] || {}), ...patch } }));
    const CHANGE_TAGS = ["Too high", "Not needed", "Needs detail", "Missing scope", "Other"];

    async function submitAccept() {
        const sig = canvasToData(sigCanvas);
        if (!sig || !printedName.trim()) { setErr(isCeiling ? "Please sign and print your name to authorize." : "Please sign and print your name to accept."); return; }
        setBusy(true); setErr("");
        try {
            await axios.post(`${BACKEND_URL}/api/public/documents/${token}/respond`, {
                action: "accept",
                printed_name: printedName,
                client_signature: { data_url: sig },
                authorized_amount: isCeiling ? (authorizedAmount === "" ? (r.band_high ?? grand) : Number(authorizedAmount)) : undefined,
            });
            setSignedName(printedName.trim());
            setStatus("accepted");
        } catch (e) { setErr(e?.response?.data?.detail || "Failed to submit."); }
        setBusy(false);
    }

    async function submitChanges() {
        const items = flagItems
            .filter((it) => flags[it.key]?.checked)
            .map((it) => ({
                label: it.label,
                tag: flags[it.key]?.tag || "Other",
                comment: (flags[it.key]?.comment || "").trim(),
                suggested_amount: flags[it.key]?.suggested !== undefined && flags[it.key]?.suggested !== ""
                    ? Number(flags[it.key].suggested) : null,
            }));
        if (!message.trim() && items.length === 0) {
            setErr("Flag at least one item or add a note describing the changes you'd like.");
            return;
        }
        setBusy(true); setErr("");
        try {
            await axios.post(`${BACKEND_URL}/api/public/documents/${token}/respond`, {
                action: "request_changes",
                printed_name: printedName,
                message,
                items,
            });
            setStatus("changes_requested");
        } catch (e) { setErr(e?.response?.data?.detail || "Failed to submit."); }
        setBusy(false);
    }

    async function downloadPDF() {
        if (!docRef.current) return;
        setDownloading(true);
        try {
            const { exportNodeToPDF } = await import("../lib/pdf");
            await exportNodeToPDF(docRef.current, `Proposal-${p.ref || "estimate"}.pdf`);
        } catch { /* ignore */ }
        finally { setDownloading(false); }
    }

    return (
        <div className="min-h-screen bg-[var(--rom-bg)] pb-12">
            <div className="bg-[var(--rom-navy)] text-white no-print">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
                    <img src="/servpro-logo.png" alt="SERVPRO" className="w-10 h-10 object-contain" />
                    <div className="flex-1">
                        <div className="font-display font-black text-base">{docTitle}</div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Prepared for the {recip.toLowerCase()}{p.recipient_name ? ` · ${p.recipient_name}` : ""}</div>
                    </div>
                    <button onClick={downloadPDF} disabled={downloading} className="btn btn-primary" data-testid="proposal-download-pdf">
                        {downloading ? <CircleNotch size={14} className="animate-spin" /> : <DownloadSimple size={14} weight="bold" />} {downloading ? "Preparing…" : "Download full PDF"}
                    </button>
                    <button onClick={() => window.print()} className="btn btn-ghost text-white border-white/30 hover:bg-white/10" data-testid="proposal-share-print">
                        <Printer size={14} weight="bold" /> Print
                    </button>
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-6 py-8 print-area">
                {done && (
                    <div className="rom-card mb-6 border-l-4 border-l-[var(--rom-green)]" data-testid="proposal-share-accepted">
                        <div className="rom-card-body flex items-center gap-3 text-[var(--rom-green)]">
                            <CheckCircle size={22} weight="fill" />
                            <div>
                                <div className="font-bold">{isCeiling ? "Authorized" : "Accepted & signed"}{(signedName || p.acceptance?.accepted_by) ? ` by ${signedName || p.acceptance.accepted_by}` : ""}{isCeiling && p.acceptance?.authorized_amount != null ? ` · NTE ${money(p.acceptance.authorized_amount)}` : ""}.</div>
                                <div className="text-xs text-[var(--rom-text-2)]">Thank you — SERVPRO has been notified and will be in touch.</div>
                            </div>
                        </div>
                    </div>
                )}
                {requested && (
                    <div className="rom-card mb-6 border-l-4 border-l-[var(--rom-orange)]" data-testid="proposal-share-requested">
                        <div className="rom-card-body flex items-start gap-3 text-[var(--rom-orange-dark)]">
                            <ChatCircleText size={22} weight="fill" />
                            <div>
                                <div className="font-bold">Change request sent.</div>
                                <div className="text-xs text-[var(--rom-text-2)]">{p.change_request?.message}</div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="rom-card mb-6">
                    <div className="rom-card-body">
                        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
                            <div>
                                <h1 className="font-display font-black text-2xl">{lossLabel} {isCeiling ? "Restoration" : "Restoration Quote"}</h1>
                                <div className="text-sm text-[var(--rom-text-2)]">Ref {p.ref} · {new Date(doc.created_at).toLocaleDateString()}</div>
                            </div>
                            <span className={`chip ${done ? "chip-green" : requested ? "chip-orange" : "chip-navy"}`}>
                                {done ? (isCeiling ? "Authorized" : "Accepted") : requested ? "Changes requested" : "For your review"}
                            </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm border-t border-[var(--rom-border)] pt-4">
                            <Field k="Property" v={inputs.address} />
                            <Field k="Loss type" v={lossLabel} />
                            <Field k="Affected area" v={inputs.sqft ? `${Number(inputs.sqft).toLocaleString()} sq ft` : "—"} />
                        </div>
                    </div>
                </div>

                <DocSection title={summaryTitle}>
                    <div className="rom-card-body">
                        <table className="w-full text-sm" data-testid="proposal-share-buckets">
                            <tbody>
                                {buckets.map(([label, val]) => (
                                    <tr key={label} className="border-b border-[var(--rom-border)]">
                                        <td className="py-2 text-[var(--rom-text-2)]">{label}</td>
                                        <td className="py-2 text-right font-mono font-semibold">{money(val)}</td>
                                    </tr>
                                ))}
                                {isCeiling ? (
                                    <>
                                        <tr className="border-b border-[var(--rom-border)]">
                                            <td className="py-2 text-[var(--rom-text-2)]">Point estimate</td>
                                            <td className="py-2 text-right font-mono font-semibold">{money(grand)}</td>
                                        </tr>
                                        <tr>
                                            <td className="pt-3 font-display font-black text-base">Not-To-Exceed (NTE)</td>
                                            <td className="pt-3 text-right font-mono font-black text-lg" data-testid="proposal-nte-amount">{money(ceiling)}</td>
                                        </tr>
                                    </>
                                ) : (
                                    <tr>
                                        <td className="pt-3 font-display font-black text-base">Total Quote</td>
                                        <td className="pt-3 text-right font-mono font-black text-lg" data-testid="proposal-total-quote">{money(grand)}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        <div className="text-[11px] text-[var(--rom-text-2)] mt-3" data-testid="proposal-summary-note">
                            {isCeiling
                                ? <>Not-To-Exceed authorization ceiling. {calibrated ? `Calibrated against closed ${lossLabel.toLowerCase()} jobs` : "Based on the current rate schedule (not yet calibrated for this loss type)"}{r.days ? ` · projected ${r.days} working days` : ""}. Work proceeds up to the authorized ceiling; final billing reflects actual time &amp; materials.</>
                                : <>This quote is prepared from the information provided{r.days ? ` and reflects an estimated ${r.days} working days` : ""}. Final price is confirmed on inspection and may adjust with discovered conditions.</>}
                        </div>
                    </div>
                </DocSection>

                {(inputs.rooms?.length > 0 || inputs.siteConditions?.length > 0) && (
                    <DocSection title="Scope summary">
                        <div className="rom-card-body">
                            <RoomScopeSummary rooms={inputs.rooms} siteConditions={inputs.siteConditions} />
                        </div>
                    </DocSection>
                )}

                {!done && !requested && (
                    <DocSection title="Your response">
                        <div className="rom-card-body space-y-4">
                            {!mode && (
                                <div className="flex flex-wrap gap-3">
                                    <button className="btn btn-primary" onClick={() => { setMode("accept"); setErr(""); if (isCeiling && authorizedAmount === "") setAuthorizedAmount(String(Math.round(ceiling))); }} data-testid="proposal-accept-btn">
                                        <CheckCircle size={15} weight="bold" /> {isCeiling ? "Authorize" : "Accept & sign"}
                                    </button>
                                    <button className="btn btn-ghost" onClick={() => { setMode("changes"); setErr(""); }} data-testid="proposal-request-btn">
                                        <ChatCircleText size={15} weight="bold" /> Request changes
                                    </button>
                                </div>
                            )}

                            {mode === "accept" && (
                                <div className="space-y-4" data-testid="proposal-accept-form">
                                    {isCeiling && (
                                        <div data-testid="proposal-authorized-wrap">
                                            <label className="label">Amount authorized (Not-To-Exceed)</label>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[var(--rom-text-2)] font-mono">$</span>
                                                <input className="input font-mono" type="number" min={0} step="0.01" value={authorizedAmount}
                                                    onChange={(e) => setAuthorizedAmount(e.target.value)} data-testid="proposal-authorized-amount" />
                                            </div>
                                            <p className="text-[11px] text-[var(--rom-text-3)] mt-1">Defaults to our requested NTE of {money(ceiling)} — adjust to the figure you are authorizing.</p>
                                        </div>
                                    )}
                                    <div>
                                        <label className="label">Printed name</label>
                                        <input className="input" value={printedName} onChange={(e) => setPrintedName(e.target.value)} placeholder="Full legal name" data-testid="proposal-printed-name" />
                                    </div>
                                    <SignatureField label="Signature" canvasRef={sigCanvas} testId="proposal-signature" />
                                    <p className="text-[11px] text-[var(--rom-text-2)] leading-relaxed">
                                        {isCeiling
                                            ? "By signing I authorize the SERVPRO franchise to proceed with restoration up to the Not-To-Exceed amount above. Final billing reflects actual time & materials and will not exceed the authorized ceiling without further written approval."
                                            : "By signing I accept this restoration quote from the SERVPRO franchise, understanding final pricing is confirmed on inspection."}
                                    </p>
                                    {err && <div className="text-sm text-[var(--rom-danger)]">{err}</div>}
                                    <div className="flex gap-2 justify-end">
                                        <button className="btn btn-ghost" onClick={() => setMode(null)}>Back</button>
                                        <button className="btn btn-primary" onClick={submitAccept} disabled={busy} data-testid="proposal-accept-submit">
                                            <CheckCircle size={14} weight="bold" /> {busy ? "Submitting…" : (isCeiling ? "Authorize work" : "Accept quote")}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {mode === "changes" && (
                                <div className="space-y-4" data-testid="proposal-changes-form">
                                    <div>
                                        <label className="label">Your name (optional)</label>
                                        <input className="input" value={printedName} onChange={(e) => setPrintedName(e.target.value)} placeholder="Full name" data-testid="proposal-changes-name" />
                                    </div>
                                    {flagItems.length > 0 && (
                                        <div>
                                            <label className="label">Flag specific items (optional)</label>
                                            <p className="text-[11px] text-[var(--rom-text-2)] -mt-1 mb-2">Tick anything you're not happy with so we know exactly what to revise.</p>
                                            <div className="space-y-2" data-testid="proposal-flag-list">
                                                {flagItems.map((it) => {
                                                    const f = flags[it.key] || {};
                                                    return (
                                                        <div key={it.key} className={`rounded-[4px] border px-3 py-2 ${f.checked ? "border-[var(--rom-orange)] bg-[var(--rom-orange-soft)]/40" : "border-[var(--rom-border)]"}`} data-testid={`proposal-flag-${it.key}`}>
                                                            <label className="flex items-center gap-2.5 text-sm cursor-pointer">
                                                                <input type="checkbox" className="accent-[var(--rom-orange)] w-4 h-4" checked={!!f.checked} onChange={(e) => setFlag(it.key, { checked: e.target.checked })} data-testid={`proposal-flag-check-${it.key}`} />
                                                                <span className="font-medium">{it.label}</span>
                                                                {it.money && it.amount != null && <span className="ml-auto font-mono text-xs text-[var(--rom-text-2)]">{money(it.amount)}</span>}
                                                            </label>
                                                            {f.checked && (
                                                                <div className="mt-2 pl-6 space-y-2">
                                                                    <div className="flex flex-wrap gap-1.5">
                                                                        {CHANGE_TAGS.map((t) => (
                                                                            <button key={t} type="button" onClick={() => setFlag(it.key, { tag: t })}
                                                                                className={`chip ${f.tag === t ? "chip-orange" : "chip-navy"} cursor-pointer`} data-testid={`proposal-flag-tag-${it.key}-${t}`}>{t}</button>
                                                                        ))}
                                                                    </div>
                                                                    <input className="input text-sm" value={f.comment || ""} onChange={(e) => setFlag(it.key, { comment: e.target.value })} placeholder="Add a comment (optional)" data-testid={`proposal-flag-comment-${it.key}`} />
                                                                    {it.money && (
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-[11px] text-[var(--rom-text-2)]">Suggested $ (optional):</span>
                                                                            <input type="number" min={0} className="input text-sm font-mono w-32 py-1 px-2" value={f.suggested ?? ""} onChange={(e) => setFlag(it.key, { suggested: e.target.value })} data-testid={`proposal-flag-amount-${it.key}`} />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                    <div>
                                        <label className="label">Anything else? {flagItems.length > 0 && <span className="text-[var(--rom-text-3)] font-normal">(optional)</span>}</label>
                                        <textarea className="input" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="e.g. Please reduce the equipment line, the basement is already dry…" data-testid="proposal-changes-message" />
                                    </div>
                                    {err && <div className="text-sm text-[var(--rom-danger)]">{err}</div>}
                                    <div className="flex gap-2 justify-end">
                                        <button className="btn btn-ghost" onClick={() => setMode(null)}>Back</button>
                                        <button className="btn btn-primary" onClick={submitChanges} disabled={busy} data-testid="proposal-changes-submit">
                                            <PaperPlaneTilt size={14} weight="bold" /> {busy ? "Sending…" : "Send request"}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </DocSection>
                )}

                <p className="text-[11px] text-center text-[var(--rom-text-2)] mt-8 italic flex items-center justify-center gap-1.5">
                    <Buildings size={12} /> Provider is an independently owned and operated SERVPRO franchise.
                </p>
            </div>

            {/* Off-screen full-detail proposal for PDF export */}
            <div ref={docRef} aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: 0, width: "800px", background: "#fff" }}>
                <ProposalPDFDoc p={p} r={r} inputs={inputs} lossLabel={lossLabel} grand={grand} buckets={buckets} createdAt={doc.created_at} isCeiling={isCeiling} ceiling={ceiling} calibrated={calibrated} />
            </div>
        </div>
    );
}

function ProposalPDFDoc({ p, r, inputs, lossLabel, grand, buckets, createdAt, isCeiling, ceiling, calibrated }) {
    const today = createdAt ? new Date(createdAt).toLocaleDateString() : new Date().toLocaleDateString();
    const labour = r.labour_lines || [];
    const equip = r.equipment_lines || [];
    const subs = r.subcontractor_lines || [];
    const bi = r.breakdown_inputs || {};
    const miscRows = [
        ["Per diem", bi.per_diem_total], ["Mobilisation", bi.mobilisation],
        ["Permits", bi.permits], ["Disposal", bi.disposal], ["Travel", bi.travel],
    ].filter(([, v]) => Number(v) > 0);
    const th = { textAlign: "left", padding: "6px 0", fontSize: 11, color: "#8a93a0", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid #e6e9ee" };
    const td = { padding: "6px 0", fontSize: 12.5, borderBottom: "1px solid #f1f3f6" };
    const tdR = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };
    const sectionTitle = { fontSize: 12, textTransform: "uppercase", letterSpacing: "0.1em", color: "#e8682e", fontWeight: 800, margin: "22px 0 8px" };

    return (
        <div style={{ color: "#1a2332", padding: "28px" }}>
            <div style={{ background: "#1a2332", color: "#fff", padding: "24px 28px", borderRadius: 6 }}>
                <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#e8682e", fontWeight: 700 }}>SERVPRO Restoration</div>
                <div style={{ fontSize: 24, fontWeight: 800, marginTop: 4 }}>{isCeiling ? `${lossLabel} — Work Authorization Request` : `${lossLabel} Restoration Quote`}</div>
                <div style={{ fontSize: 12, color: "#c7cdd6", marginTop: 4 }}>Ref {p.ref} · {today}</div>
            </div>

            <div style={{ display: "flex", gap: 28, fontSize: 12.5, margin: "20px 0", paddingBottom: 16, borderBottom: "1px solid #e6e9ee", flexWrap: "wrap" }}>
                <div><div style={{ fontSize: 10.5, textTransform: "uppercase", color: "#8a93a0", fontWeight: 700 }}>Prepared for</div><div style={{ fontWeight: 600, marginTop: 3 }}>{p.recipient_name || "Property Owner / Carrier"}</div>{inputs.address && <div style={{ color: "#5a6472" }}>{inputs.address}</div>}</div>
                <div><div style={{ fontSize: 10.5, textTransform: "uppercase", color: "#8a93a0", fontWeight: 700 }}>Loss type</div><div style={{ fontWeight: 600, marginTop: 3 }}>{lossLabel}</div></div>
                <div><div style={{ fontSize: 10.5, textTransform: "uppercase", color: "#8a93a0", fontWeight: 700 }}>Affected area</div><div style={{ fontWeight: 600, marginTop: 3 }}>{Number(inputs.sqft || 0).toLocaleString()} sq ft</div></div>
                {r.crew_size ? <div><div style={{ fontSize: 10.5, textTransform: "uppercase", color: "#8a93a0", fontWeight: 700 }}>Implied crew · days</div><div style={{ fontWeight: 600, marginTop: 3 }}>{r.crew_size} · {r.days}d</div></div> : null}
            </div>

            <div style={sectionTitle}>{isCeiling ? "Authorization Request Summary" : "Quote Summary"}</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                    {buckets.map(([label, val]) => (
                        <tr key={label}><td style={td}>{label}</td><td style={tdR}>{money(val)}</td></tr>
                    ))}
                    {isCeiling ? (
                        <>
                            <tr><td style={td}>Point estimate</td><td style={tdR}>{money(grand)}</td></tr>
                            <tr><td style={{ ...td, fontWeight: 800, fontSize: 15, borderBottom: "none", paddingTop: 12 }}>Not-To-Exceed (NTE)</td><td style={{ ...tdR, fontWeight: 800, fontSize: 16, borderBottom: "none", paddingTop: 12 }}>{money(ceiling)}</td></tr>
                        </>
                    ) : (
                        <tr><td style={{ ...td, fontWeight: 800, fontSize: 15, borderBottom: "none", paddingTop: 12 }}>Total Quote</td><td style={{ ...tdR, fontWeight: 800, fontSize: 16, borderBottom: "none", paddingTop: 12 }}>{money(grand)}</td></tr>
                    )}
                </tbody>
            </table>
            <div style={{ fontSize: 11, color: "#8a93a0", marginTop: 6 }}>
                {isCeiling
                    ? `Not-To-Exceed authorization ceiling. ${calibrated ? `Calibrated against closed ${lossLabel.toLowerCase()} jobs` : "Based on the current rate schedule (not yet calibrated for this loss type)"}${r.days ? ` · projected ${r.days} working days` : ""}. Work proceeds up to the authorized ceiling; final billing reflects actual time & materials.`
                    : `Restoration quote prepared from the information provided${r.days ? ` · estimated ${r.days} working days` : ""}. Final price is confirmed on inspection and may adjust with discovered conditions.`}
            </div>

            {(inputs.rooms?.length > 0 || inputs.siteConditions?.length > 0) && (
                <>
                    <div style={sectionTitle}>Scope summary · for Xactimate line entry</div>
                    {groupRoomsByFloor(inputs.rooms).length > 0 && (
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead><tr><th style={th}>Floor / area</th><th style={th}>Room</th><th style={{ ...th, textAlign: "right" }}>Sq ft</th><th style={th}>Cut height</th><th style={th}>Wall scope</th><th style={th}>Materials</th></tr></thead>
                            <tbody>
                                {groupRoomsByFloor(inputs.rooms).map((g) => g.rooms.map((room, i) => (
                                    <tr key={`${g.floor}-${i}`}>
                                        <td style={td}>{g.floor}</td>
                                        <td style={td}>{room.name || "—"}</td>
                                        <td style={tdR}>{Number(room.sqft || 0).toLocaleString()}</td>
                                        <td style={td}>{DEMO_LEVEL_LABEL[room.demoLevel || ""] || room.demoLevel}</td>
                                        <td style={td}>{room.wallScope || "All walls"}</td>
                                        <td style={{ ...td, color: "#5a6472" }}>{room.materials || "—"}</td>
                                    </tr>
                                )))}
                            </tbody>
                        </table>
                    )}
                    {groupRoomsByFloor(inputs.rooms).some((g) => g.rooms.some((r) => r.wallScope)) && (
                        <div style={{ fontSize: 10, fontStyle: "italic", color: "#8a93a0", marginTop: 4 }}>
                            Wall scope is descriptive only — pricing is not yet adjusted for partial-wall damage.
                        </div>
                    )}
                    {(inputs.siteConditions || []).length > 0 && (
                        <div style={{ fontSize: 12, marginTop: 8 }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>Site conditions</div>
                            <ul style={{ margin: 0, paddingLeft: 18, color: "#5a6472" }}>
                                {(inputs.siteConditions || []).map((c, i) => {
                                    const cond = typeof c === "string" ? { text: c } : (c || {});
                                    return <li key={i}>{cond.text}</li>;
                                })}
                            </ul>
                        </div>
                    )}
                </>
            )}

            {labour.length > 0 && (<>
                <div style={sectionTitle}>Labour</div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}><thead><tr><th style={th}>Role</th><th style={{ ...th, textAlign: "right" }}>Crew</th><th style={{ ...th, textAlign: "right" }}>Cost</th></tr></thead>
                <tbody>{labour.map((l, i) => (<tr key={i}><td style={td}>{l.role}</td><td style={tdR}>{l.headcount ?? "—"}</td><td style={tdR}>{money(l.subtotal)}</td></tr>))}</tbody></table>
            </>)}

            {equip.length > 0 && (<>
                <div style={sectionTitle}>Equipment</div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}><thead><tr><th style={th}>Item</th><th style={{ ...th, textAlign: "right" }}>Qty × Days</th><th style={{ ...th, textAlign: "right" }}>Cost</th></tr></thead>
                <tbody>{equip.map((l, i) => (<tr key={i}><td style={td}>{l.item}</td><td style={tdR}>{l.qty} × {l.days}d</td><td style={tdR}>{money(l.subtotal)}</td></tr>))}</tbody></table>
            </>)}

            {subs.length > 0 && (<>
                <div style={sectionTitle}>Subcontractors</div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}><thead><tr><th style={th}>Item</th><th style={{ ...th, textAlign: "right" }}>Cost</th></tr></thead>
                <tbody>{subs.map((l, i) => (<tr key={i}><td style={td}>{l.item || l.label}</td><td style={tdR}>{money(l.subtotal)}</td></tr>))}</tbody></table>
            </>)}

            {miscRows.length > 0 && (<>
                <div style={sectionTitle}>Mobilisation & Incidentals</div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>{miscRows.map(([label, v]) => (<tr key={label}><td style={td}>{label}</td><td style={tdR}>{money(v)}</td></tr>))}</tbody></table>
            </>)}

            <div style={{ marginTop: 24, paddingTop: 14, borderTop: "1px solid #e6e9ee", fontSize: 10, color: "#9aa2ad", lineHeight: 1.6 }}>
                {isCeiling
                    ? "This Work Authorization Request is prepared by an independently owned and operated SERVPRO franchise. The Not-To-Exceed figure is an authorization ceiling, not a fixed price; final billing reflects actual time & materials and will not exceed the authorized amount without further written approval."
                    : "This quote is prepared by an independently owned and operated SERVPRO franchise. Final pricing is confirmed on inspection and may adjust with discovered conditions."}
            </div>
        </div>
    );
}

function canvasToData(ref) {
    if (!ref.current) return null;
    const c = ref.current;
    // Detect if anything was drawn — check pixel data for any non-white.
    const ctx = c.getContext("2d");
    const d = ctx.getImageData(0, 0, c.width, c.height).data;
    for (let i = 0; i < d.length; i += 4) {
        if (d[i] < 240 || d[i+1] < 240 || d[i+2] < 240) return c.toDataURL("image/png");
    }
    return null;
}

function fmt(s) { try { return new Date(s).toLocaleString(); } catch { return s; } }
